<template>
  <nav
    class="pagination"
    role="navigation"
    aria-label="pagination"
    :class="{ 'is-sticky': isSticky }"
  >
    <component
      :is="prevLink.is"
      :class="isEnabledPrevLink"
      class="pagination-previous"
      v-bind="prevLink"
      >{{ $texts('back', 'Zurück') }}</component
    >
    <component
      :is="nextLink.is"
      :class="isEnabledNextLink"
      class="pagination-next"
      v-bind="nextLink"
      >{{ $texts('next', 'Weiter') }}</component
    >
    <ul class="pagination-list">
      <li v-if="first">
        <nuxt-link
          :to="first.to"
          class="pagination-link"
          :class="{ 'is-current': first.isActive }"
          >1</nuxt-link
        >
      </li>
      <li v-if="hasFirstEllipsis">
        <span class="pagination-ellipsis">&hellip;</span>
      </li>
      <li v-for="pageItem in pages" :key="pageItem.page">
        <nuxt-link
          :to="pageItem.to"
          class="pagination-link"
          :class="{ 'is-current': pageItem.isActive }"
          >{{ pageItem.page }}</nuxt-link
        >
      </li>
      <li v-if="hasLastEllipsis">
        <span class="pagination-ellipsis">&hellip;</span>
      </li>
      <li v-if="last">
        <nuxt-link
          :to="last.to"
          class="pagination-link"
          :class="{ 'is-current': last.isActive }"
          >{{ pageCount }}</nuxt-link
        >
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components'

const props = defineProps({
  page: {
    type: Number,
    default: 1,
  },
  perPage: {
    type: Number,
    default: 0,
  },
  total: {
    type: Number,
    default: 0,
  },
  isSticky: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const { $texts } = useNuxtApp()

const getLink = (page: number) => {
  const query: any = {
    ...route.query,
    page,
  }

  if (page === 1) {
    delete query.page
  }
  return {
    isActive: page === props.page,
    page,
    to: {
      path: route.path,
      query,
    },
  }
}

const first = computed(() => {
  if (props.page >= 3) {
    return getLink(1)
  }

  return null
})

const pageCount = computed(() => {
  return Math.ceil(props.total / props.perPage)
})

const last = computed(() => {
  if (props.page <= pageCount.value - 2) {
    return getLink(pageCount.value)
  }
  return null
})

const hasFirstEllipsis = computed(() => {
  return props.page >= 5
})

const hasLastEllipsis = computed(() => {
  return props.page < pageCount.value - 3
})

const pages = computed(() => {
  let left = Math.max(1, props.page - 1)
  if (left - 1 === 2) {
    left-- // Do not show the ellipsis if there is only one to hide
  }
  let right = Math.min(props.page + 1, pageCount.value)
  if (pageCount.value - right === 2) {
    right++ // Do not show the ellipsis if there is only one to hide
  }

  const pages = []
  for (let i = left; i <= right; i++) {
    pages.push(getLink(i))
  }
  return pages
})

const prevLink = computed(() => {
  const attributes: any = {
    is: 'span',
  }
  if (props.page > 1) {
    attributes.is = NuxtLink
    attributes.to = getLink(props.page - 1).to
  }
  return attributes
})

const isEnabledPrevLink = computed(() => {
  if (props.page > 1) {
    return ''
  }
  return 'is-disabled'
})

const nextLink = computed(() => {
  const attributes: any = {
    is: 'span',
  }
  if (props.page * props.perPage < props.total) {
    attributes.is = NuxtLink
    attributes.to = getLink(props.page + 1).to
  } else {
    attributes.disabled = true
  }
  return attributes
})

const isEnabledNextLink = computed(() => {
  if (props.page * props.perPage < props.total) {
    return ''
  }
  return 'is-disabled'
})
</script>
